import { Customization } from '@/common/types/student';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { defaultCharacterInventoryState } from '@/common/utils/student';
import Customizations from '@/game/GameComponents/GameData/customizations.json';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getCharacterInventory,
  getSession,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import {
  getCustomizationFromId,
  getPriceForItem,
} from '@/game/GameComponents/PhaserGame/Utils/CosmeticUtils';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { getFinalRubiesFromAttemptsAndAccuracy } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import { Reward } from '@/game/GameComponents/PhaserGame/Utils/RewardsManager';
import Minigame from '@/game/scenes/Minigame';

export class InventoryManager {
  static _instance?: InventoryManager;

  constructor() {
    if (InventoryManager.instance !== undefined) {
      return;
    }
    InventoryManager._instance = this;

    // Event listeners. These need to be registered directly to the event bus because they are not part of a scene.
    EventBus.on(
      GAME_EVENTS.PLAYER_EQUIPPED_COSMETIC,
      this.handlePlayerEquippedCosmetic,
      this,
    );

    EventBus.on(
      GAME_EVENTS.PLAYER_UNEQUIPPED_COSMETIC,
      this.handlePlayerUnequippedCosmetic,
      this,
    );

    EventBus.on(
      GAME_EVENTS.PLAYER_PREVIEWED_COSMETIC,
      this.handlePlayerPreviewedCosmetic,
      this,
    );
    EventBus.on(
      GAME_EVENTS.PLAYER_PURCHASED_COSMETIC,
      this.handlePlayerPurchasedCosmetic,
      this,
    );
    EventBus.on(GAME_EVENTS.BUY_ALL_ITEMS, this.buyAllItems, this);
    EventBus.on(
      GAME_EVENTS.PLAYER_CLAIMED_REWARD,
      this.handlePlayerClaimedReward,
      this,
    );
    EventBus.on(
      GAME_EVENTS.MINIGAME_COMPLETED_SUCCESSFULLY,
      this.handleMinigameCompletedSuccessfully,
      this,
    );
  }

  static get instance(): InventoryManager {
    return InventoryManager._instance!;
  }

  static destroyInventoryManager() {
    InventoryManager._instance = undefined;
  }

  buyAllItems() {
    const characterInventory = getCharacterInventory();
    const currentStudentId = getStudent().id;
    if (currentStudentId && characterInventory) {
      const customizations = Customizations as Customization[];
      customizations.forEach((customization) => {
        this.unlockItem(customization.id);
      });
    }
  }

  unlockItem(itemId: number) {
    const characterInventory = getCharacterInventory();
    if (!characterInventory.unlockedItems.includes(itemId)) {
      // Add the item to the unlocked items array.
      GameInstance.instance.updateCharacterInventory({
        unlockedItems: [...characterInventory.unlockedItems, itemId],
      });
    }
    if (!characterInventory.newItems.includes(itemId)) {
      // Add the item to the new items array.
      GameInstance.instance.updateCharacterInventory({
        newItems: [...characterInventory.newItems, itemId],
      });
    }
  }

  adjustCoins(delta: number) {
    // Modify the student's coins by the price.
    GameInstance.instance.updateCharacterInventory({
      coins: Math.max(0, getCharacterInventory().coins + delta),
    });
    EventBus.emit(
      GAME_EVENTS.COIN_VALUE_CHANGED,
      getCharacterInventory().coins,
    );
  }

  handlePlayerPurchasedCosmetic(cosmetic: Customization) {
    const characterInventory = getCharacterInventory();
    const itemId = cosmetic.id;

    if (characterInventory) {
      trackMixpanelEvent('Item Purchased', {
        itemId: itemId,
        itemName: getCustomizationFromId(itemId)?.name,
      });

      this.unlockItem(itemId);
      const price = getPriceForItem(itemId);
      // Spend the coins.
      this.adjustCoins(-price);
    }
  }

  handlePlayerClaimedReward(reward: Reward) {
    this.adjustCoins(reward.rubies);
  }

  handleMinigameCompletedSuccessfully(_minigame: Minigame) {
    // Give currency.
    const rubies = getFinalRubiesFromAttemptsAndAccuracy(
      getSession().attempts,
      getSession().currentActivity?.accuracy || 0,
    );
    this.adjustCoins(rubies);
  }

  // State updates.
  equipCosmetic(cosmetic: Customization) {
    GameInstance.instance.updateEquipmentConfiguration({
      [cosmetic.slot]: cosmetic.id,
    });

    GameInstance.instance.updateCharacterInventory({
      newItems: getCharacterInventory().newItems.filter(
        (i) => i !== cosmetic.id,
      ),
    });
  }

  unequipCosmetic(cosmetic: Customization) {
    const key = cosmetic.slot;
    let newId = null;
    if (key === 'clothes' || key === 'pants') {
      newId = defaultCharacterInventoryState.equippedItems[key];
    }
    GameInstance.instance.updateEquipmentConfiguration({
      [cosmetic.slot]: newId,
    });
  }

  previewCosmetic(cosmetic: Customization) {
    GameInstance.instance.updateCharacterInventory({
      newItems: getCharacterInventory().newItems.filter(
        (i) => i !== cosmetic.id,
      ),
    });
  }

  // Event handlers.
  handlePlayerEquippedCosmetic(cosmetic: Customization) {
    this.equipCosmetic(cosmetic);
  }

  handlePlayerUnequippedCosmetic(cosmetic: Customization) {
    this.unequipCosmetic(cosmetic);
  }

  handlePlayerPreviewedCosmetic(cosmetic: Customization) {
    this.previewCosmetic(cosmetic);
  }
}

export const inventoryManager = new InventoryManager();
